import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardTabRoute } from '../hooks/useDashboardTabRoute';
import { attendanceAPI, biometricAPI, performanceAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';
import CameraCapture from '../components/CameraCapture';
import {
  Briefcase, Clock, Calendar, Users, User, DollarSign, Heart, LayoutDashboard, LifeBuoy, BarChart3,
  Trophy, Package,
} from 'lucide-react';
import { format, isValid } from 'date-fns';
import TeamSection from '../components/TeamSection';
import TeamStatusBoard from '../components/TeamStatusBoard';
import ProfileSection from '../components/ProfileSection';
import PayrollManagement from '../components/PayrollManagement';
import BenefitsManagement from '../components/BenefitsManagement';
import EmployeeAttendanceMenu from '../components/EmployeeAttendanceMenu';
import EmployeeOverviewDashboard from '../components/EmployeeOverviewDashboard';
import EmployeeWorkspaceHub from '../components/EmployeeWorkspaceHub';
import HelpdeskModule from '../components/HelpdeskModule';
import EmployeePulseSurveyInbox from '../components/EmployeePulseSurveyInbox';
import WorkRequestModule from '../components/WorkRequestModule';
import LeaveSection from '../components/LeaveSection';
import GamificationModuleDeferred from '../components/GamificationModuleDeferred';
import AssetManagementModule from '../components/AssetManagementModule';
import AIAssistant from '../components/AIAssistant';
import ReportsHub from './ReportsHub';
import GrowthModule from '../components/GrowthModule';
import WorkplaceModule from '../components/WorkplaceModule';
import MyAccountModule from '../components/MyAccountModule';
import Layout from '../components/Layout';
import Swal from 'sweetalert2';
import { EMPLOYEE_NAV_SCHEMA, mapNavItemsWithIcons, filterAiAssistantNav } from '../lib/navigation';
import { filterNavByModuleAccess } from '../lib/planAccess';
import { hasAiAssistantAddon } from '../lib/aiDashboardAddons';

const EmployeeDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useDashboardTabRoute('/dashboard', 'workspace');
  
  const [showCamera, setShowCamera] = useState(false);
  const [attemptType, setAttemptType] = useState('check_in');
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [todayRecord, setTodayRecord] = useState(null);
  const [performanceSummary, setPerformanceSummary] = useState(null);

  const getValidDate = (value) => {
    if (!value) return null;
    const parsedDate = new Date(value);
    return isValid(parsedDate) ? parsedDate : null;
  };

  const formatDateTime = (value, pattern, fallback = '-') => {
    const parsedDate = getValidDate(value);
    return parsedDate ? format(parsedDate, pattern) : fallback;
  };

  const chartData = useMemo(() => {
    if (!records || records.length === 0) return [];
    return [...records]
      .slice(0, 7)
      .reverse()
      .map(rec => {
        const recordDate = getValidDate(rec.check_in_time || rec.date);
        if (!recordDate) return null;

        return {
          date: format(recordDate, 'EEE'),
          hours: rec.total_hours ? parseFloat(rec.total_hours.toFixed(1)) : 0,
          status: rec.status
        };
      })
      .filter(Boolean);
  }, [records]);

  const navItems = useMemo(
    () => {
      const moduleFiltered = filterNavByModuleAccess(EMPLOYEE_NAV_SCHEMA, user?.moduleAccess, user?.isSuperadmin);
      const filteredSchema = filterAiAssistantNav(moduleFiltered, hasAiAssistantAddon(user));
      return mapNavItemsWithIcons(filteredSchema, {
        workspace: Briefcase,
        dashboard: LayoutDashboard,
        attendance: Clock,
        assets: Package,
        leaves: Calendar,
        team: Users,
        employees: Users,
        helpdesk: LifeBuoy,
        gamification: Trophy,
        reports: BarChart3,
        finance: DollarSign,
        benefits: Heart,
        profile: User,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on the specific user.* fields used
    [user?.moduleAccess, user?.isSuperadmin]
  );

  useEffect(() => {
    const allowedIds = new Set(navItems.flatMap((item) => [item.id, ...(item.subItems || []).map((sub) => sub.id)]));
    if (allowedIds.size > 0 && !allowedIds.has(activeTab)) {
      setActiveTab(navItems[0]?.id || 'dashboard');
    }
  }, [navItems, activeTab, setActiveTab]);

  useEffect(() => {
    if (activeTab === 'attendance' || activeTab === 'dashboard') {
      fetchRecords();
    }
    if (activeTab === 'dashboard' || activeTab === 'workspace') {
      fetchPerformanceSummary();
    }
    // Attendance records only need to refresh when the visible tab changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchRecords = async () => {
    try {
      const response = await attendanceAPI.getMyRecords();
      setRecords(response.data);
      
      const today = new Date().toDateString();
      const todayRec = response.data.find((r) => {
        const checkInDate = getValidDate(r.check_in_time);
        return checkInDate && checkInDate.toDateString() === today;
      });
      setTodayRecord(todayRec);
    } catch (error) {
      console.error('Error fetching records:', error);
    }
  };

  const fetchPerformanceSummary = async () => {
    try {
      const response = await performanceAPI.getMyKpi();
      setPerformanceSummary(response.data || null);
    } catch (error) {
      console.error('Failed to fetch performance summary:', error);
    }
  };

  const handleMarkAttendance = (type) => {
    if (!user?.employeeId) {
      Swal.fire({
        icon: 'warning',
        title: 'Profile setup required',
        text: 'Your account is missing an employee profile. Please sign out and sign back in, or contact HR if the problem continues.'
      });
      return;
    }
    setAttemptType(type);
    setShowCamera(true);
  };

  const handleCameraCapture = async (base64Image) => {
    setShowCamera(false);
    setLoading(true);

    try {
      const attendancePayload = {
        employee_id: user.employeeId,
        face_image_base64: base64Image,
        attempt_type: attemptType,
        device_id: getDeviceId(),
        device_name: navigator.userAgent,
      };
      let response = await attendanceAPI.mark(attendancePayload);

      const requiresEnrollment =
        response.data.status === 'failed' && (
          response.data.message === 'Biometric enrollment required' ||
          response.data.message === 'No face embedding found. Please complete enrollment.'
        );

      if (requiresEnrollment) {
        const consentResult = await Swal.fire({
          icon: 'info',
          title: 'Complete Face Enrollment',
          text: 'Your first live selfie can be used to enroll your face for biometric attendance. Do you consent to store your biometric template for future check-ins?',
          showCancelButton: true,
          confirmButtonText: 'Enroll and continue',
          cancelButtonText: 'Cancel'
        });

        if (!consentResult.isConfirmed) {
          return;
        }

        await biometricAPI.enroll({
          employee_id: user.employeeId,
          face_image_base64: base64Image,
          sample_number: 1,
          consent_confirmed: true,
        });

        response = await attendanceAPI.mark(attendancePayload);
      }

      if (response.data.status === 'success') {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false
        });
        fetchRecords();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed',
          text: response.data.message
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: getErrorMessage(error, 'Failed to mark attendance')
      });
    } finally {
      setLoading(false);
    }
  };

  const getDeviceId = () => {
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = 'web_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
  };

  const canCheckIn = !todayRecord || !todayRecord.check_in_time;
  const canCheckOut = todayRecord && todayRecord.check_in_time && !todayRecord.check_out_time;

  const handleDashboardNavigate = (tabId) => {
    if (!tabId) return;
    setActiveTab(tabId);
  };

  return (
    <Layout 
      items={navItems} 
      activeItem={activeTab} 
      onNavigate={setActiveTab} 
      onLogout={logout}
    >
      <div className="space-y-6" data-testid="employee-dashboard">
        {activeTab === 'ai-assistant-chat' && <AIAssistant />}

        {activeTab === 'dashboard' && (
          <EmployeeOverviewDashboard
            records={records}
            todayRecord={todayRecord}
            chartData={chartData}
            formatDateTime={formatDateTime}
            onNavigateTab={handleDashboardNavigate}
            performanceSummary={performanceSummary}
          />
        )}

        {activeTab === 'workspace' && (
          <EmployeeWorkspaceHub
            employeeId={user?.employeeId}
            onNavigateTab={handleDashboardNavigate}
          />
        )}

        {activeTab === 'attendance' && (
          <EmployeeAttendanceMenu
            records={records}
            todayRecord={todayRecord}
            chartData={chartData}
            loading={loading}
            attemptType={attemptType}
            canCheckIn={canCheckIn}
            canCheckOut={canCheckOut}
            onMarkAttendance={handleMarkAttendance}
            formatDateTime={formatDateTime}
          />
        )}

        {activeTab === 'assets' && <AssetManagementModule mode="employee" />}

        {(activeTab === 'leave-regularization' || activeTab === 'leave-wfh' || activeTab === 'leave-outstation') && (
          <WorkRequestModule
            mode="employee"
            requestType={activeTab === 'leave-regularization' ? 'regularization' : activeTab === 'leave-wfh' ? 'wfh' : 'outstation'}
          />
        )}
        {activeTab.startsWith('leave-') && !['leave-regularization', 'leave-wfh', 'leave-outstation'].includes(activeTab) && (
          <LeaveSection currentTab={activeTab.replace('leave-', '')} />
        )}

        {activeTab.startsWith('finance-') && (
          <PayrollManagement 
            currentTab={
              activeTab === 'finance-home' ? 'overview' :
              activeTab === 'finance-payslips' ? 'payslips' :
              activeTab === 'finance-reimbursements' ? 'reimbursements' :
              'overview'
            } 
          />
        )}

        {activeTab.startsWith('benefits-') && (
          <BenefitsManagement 
            currentTab={
              activeTab === 'benefits-my' ? 'my-benefits' :
              activeTab === 'benefits-plans' ? 'plans' :
              'my-benefits'
            } 
          />
        )}

        {activeTab === 'team' && (
          <div className="space-y-4">
            {/* Renders nothing unless this person actually has direct reports. */}
            <TeamStatusBoard />
            <TeamSection />
          </div>
        )}
        {activeTab === 'helpdesk' && <HelpdeskModule mode="employee" />}
        {activeTab.startsWith('gamification-') && <GamificationModuleDeferred mode="employee" activeTab={activeTab} onNavigate={setActiveTab} />}
        {activeTab === 'pulse-surveys' && <EmployeePulseSurveyInbox />}
        {activeTab === 'performance-report' && <ReportsHub currentTab="performance" viewMode="employee" />}
        {activeTab.startsWith('growth-') && (
          <GrowthModule currentTab={activeTab.replace('growth-', '')} />
        )}
        {activeTab.startsWith('workplace-') && (
          <WorkplaceModule currentTab={activeTab.replace('workplace-', '')} onNavigate={setActiveTab} />
        )}
        {activeTab.startsWith('account-') && (
          <MyAccountModule currentTab={activeTab.replace('account-', '')} />
        )}
        {activeTab === 'profile' && <ProfileSection />}
      </div>

      {showCamera && (
        <CameraCapture
          title={`${attemptType === 'check_in' ? 'Check In' : 'Check Out'} - Face Verification`}
          onCapture={handleCameraCapture}
          onCancel={() => setShowCamera(false)}
        />
      )}
    </Layout>
  );
};

export default EmployeeDashboard;
